import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  LoadingSpinner,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiBoatThin, PiX } from 'react-icons/pi';
import React, { useCallback, useEffect } from 'react';
import { useGPSLost } from '../GpsLost/GPSLostContext';
import {
  ExtractedTargetInfo,
  extractTargetInfos,
  getTargetInfoByMMSI,
  ShipInfo,
  useSession,
} from '@ais-ui/websockets';
import { cn } from '@ais-ui/utils';
import { useLayers } from '@ais-ui/react-openlayers';

type ShipData = ExtractedTargetInfo | number;

export const GPSLostSelectedShips = () => {
  const { selectedMmsis, setSelectedMmsis } = useGPSLost();
  const { layers, removeLayer } = useLayers();
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);
  const [shipData, setShipData] = React.useState<ShipData[]>([]);
  const { olSession } = useSession();
  const [isOpen, setIsOpen] = React.useState(false);

  const onRemoveShip = useCallback(
    (mmsi: number) => {
      setSelectedMmsis((prevMmsis) =>
        prevMmsis.filter((item) => item !== mmsi)
      );

      const layersToRemove = layers.filter(
        (layer) =>
          layer.id &&
          layer.id.includes('gps-lost-track') &&
          layer.id.includes(mmsi.toString())
      );

      // Remove each matching layer
      layersToRemove.forEach((layer) => {
        if (layer.id) {
          removeLayer(layer.layer);
        }
      });
    },
    [layers, removeLayer, setSelectedMmsis]
  );

  useEffect(() => {
    if (!isOpen || selectedMmsis.length === 0) {
      return;
    }

    const shipDataArray: ShipData[] = [];
    setIsLoading(true);
    setError(null);

    // Track how many requests have completed
    let completedRequests = 0;
    const totalRequests = selectedMmsis.length;

    // Iterate through each MMSI in the selectedGPSLostGridCell
    selectedMmsis.forEach(async (mmsi) => {
      try {
        const queryPointByMmsi = await getTargetInfoByMMSI(olSession, mmsi);
        const targetInfoResultByMmsi = extractTargetInfos(queryPointByMmsi);

        if (targetInfoResultByMmsi.extractedTargetInfo.length === 0) {
          shipDataArray.push(mmsi);
        } else {
          shipDataArray.push(targetInfoResultByMmsi);
        }
      } catch (error) {
        console.error(`Error fetching ship data for MMSI ${mmsi}:`, error);
        // We don't set the error state here to allow other requests to complete
      } finally {
        completedRequests++;

        // Once all requests are complete, update the state
        if (completedRequests === totalRequests) {
          setShipData(shipDataArray);
          setIsLoading(false);

          // If no data was fetched successfully, set an error
          if (shipDataArray.length === 0) {
            setError(new Error('Failed to fetch any ship data'));
          }
        }
      }
    });
  }, [olSession, selectedMmsis, isOpen]);

  const renderShipInfo = (data: ShipData) => {
    if (typeof data === 'number') {
      return (
        <div key={data} className="py-1">
          <span className="font-medium">MMSI:</span> {data}
        </div>
      );
    } else {
      const info = data.extractedTargetInfo[0] as ShipInfo;
      return (
        <div className="flex justify-between items center">
          <div key={info.mmsi} className="py-2">
            <div className="font-medium text-sm">
              {info.name || `Ship (MMSI: ${info.mmsi})`}
            </div>
            <div className="flex space-x-2 text-xs text-muted-foreground items-center">
              <div>MMSI: {info.mmsi}</div>
              <div className={cn('rounded-full h-1 w-1 bg-blue-600')}></div>
              <div>IMO: {info.imo ? info.imo : 'None'}</div>
            </div>
          </div>
          <div className="flex flex-col items-center justify-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onRemoveShip(Number(info.mmsi))}
            >
              <PiX></PiX>
            </Button>
          </div>
        </div>
      );
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <div className="relative">
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-9 w-9 flex-shrink-0 p-0 m-0 relative"
              >
                <PiBoatThin className="h-5 w-5" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          {selectedMmsis.length > 0 && (
            <div className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-primary text-xs flex items-center justify-center text-primary-foreground">
              {selectedMmsis.length}
            </div>
          )}
        </div>
        <TooltipContent>Selected GPS Lost ships</TooltipContent>
      </Tooltip>
      <PopoverContent side="top" align="start" className="w-72">
        {selectedMmsis.length > 0 ? (
          <>
            <div className="font-semibold mb-2 border-b pb-1">
              GPS Lost Ships ({selectedMmsis.length})
            </div>

            {isLoading ? (
              <div className="flex justify-center items-center py-4">
                <LoadingSpinner size={'small'} />
              </div>
            ) : error ? (
              <Alert variant="destructive">
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error.message}</AlertDescription>
              </Alert>
            ) : (
              <div className="max-h-60 overflow-y-auto space-y-1 divide-y">
                {shipData.map((data) => renderShipInfo(data))}
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-2 text-muted-foreground">
            No GPS lost ships selected
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
